import { useState, useEffect } from 'react'
import { api } from '../services/api'
import {
  CpuChipIcon,
  EnvelopeIcon,
  SpeakerWaveIcon,
  CogIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  BoltIcon,
} from '@heroicons/react/24/outline'

function StatusBadge({ status }) {
  const configs = {
    configured: { color: 'bg-green-50 text-green-700 border-green-200', Icon: CheckCircleIcon, label: 'Configured' },
    not_configured: { color: 'bg-red-50 text-red-700 border-red-200', Icon: XCircleIcon, label: 'Not Configured' },
    placeholder: { color: 'bg-amber-50 text-amber-700 border-amber-200', Icon: ExclamationTriangleIcon, label: 'Placeholder' },
    disabled: { color: 'bg-slate-100 text-slate-600 border-slate-200', Icon: XCircleIcon, label: 'Disabled' },
  }
  const cfg = configs[status] || configs.not_configured
  const Icon = cfg.Icon
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="h-3.5 w-3.5" />
      {cfg.label}
    </span>
  )
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-medium text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-slate-400" />
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-slate-900">{value}</p>
    </div>
  )
}

function FeatureFlag({ label, enabled }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        enabled ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
      }`}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
    </div>
  )
}

export default function Settings() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await api.get('/admin/settings')
      setData(response.data)
    } catch (err) {
      console.error('Failed to load settings:', err)
      setError('Failed to load settings data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button onClick={loadSettings} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm transition-colors">
            Retry
          </button>
        </div>
      </div>
    )
  }

  const { integrations, platform } = data
  const openai = integrations.openai
  const zeptomail = integrations.zeptomail
  const elevenlabs = integrations.elevenlabs

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">API integrations, usage statistics, and platform configuration</p>
        </div>
        <button
          onClick={loadSettings}
          className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* OpenAI Integration */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <CpuChipIcon className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">OpenAI Integration</h3>
              <p className="text-xs text-slate-500">Language models and embeddings</p>
            </div>
          </div>
          <StatusBadge status={openai.status} />
        </div>

        <div className="space-y-0">
          {openai.masked_key && (
            <InfoRow label="API Key" value={
              <code className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-700">
                {'*'.repeat(16)}{openai.masked_key.replace('...', '')}
              </code>
            } />
          )}
          <InfoRow label="Chat Model" value={openai.chat_model} mono />
          <InfoRow label="Embedding Model" value={openai.embedding_model} mono />
          <InfoRow label="Embedding Dimensions" value={openai.embedding_dimensions} />
          <InfoRow label="Max Tokens" value={openai.max_tokens} />
          <InfoRow label="Temperature" value={openai.temperature} />
        </div>

        {/* Usage Stats */}
        <div className="mt-5 pt-5 border-t border-slate-200">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Usage Statistics</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard
              label="All-time Conversations"
              value={openai.usage.total_conversations.toLocaleString()}
              icon={ChatBubbleLeftRightIcon}
            />
            <StatCard
              label="Total Tokens"
              value={openai.usage.total_tokens.toLocaleString()}
              icon={CpuChipIcon}
            />
            <StatCard
              label="Total Cost"
              value={`$${openai.usage.total_cost.toFixed(4)}`}
              icon={CurrencyDollarIcon}
            />
            <StatCard
              label="Today's Usage"
              value={`${openai.usage.today_tokens.toLocaleString()} tokens`}
              icon={ClockIcon}
            />
            <StatCard
              label="Last 30 Days"
              value={`$${openai.usage.last_30_days_cost.toFixed(4)}`}
              icon={CurrencyDollarIcon}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Prompt Tokens (All-time)</span>
              <p className="text-lg font-bold text-slate-900 mt-1">{openai.usage.total_prompt_tokens.toLocaleString()}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Completion Tokens (All-time)</span>
              <p className="text-lg font-bold text-slate-900 mt-1">{openai.usage.total_completion_tokens.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ZeptoMail & ElevenLabs Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ZeptoMail Integration */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <EnvelopeIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">ZeptoMail</h3>
                <p className="text-xs text-slate-500">Email service (OTP & notifications)</p>
              </div>
            </div>
            <StatusBadge status={zeptomail.status} />
          </div>

          <div className="space-y-0">
            {zeptomail.masked_key && (
              <InfoRow label="API Key" value={
                <code className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-700">
                  {'*'.repeat(16)}{zeptomail.masked_key.replace('...', '')}
                </code>
              } />
            )}
            <InfoRow label="Email Sending" value={
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                zeptomail.email_sending_enabled ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                {zeptomail.email_sending_enabled ? 'Active' : 'Inactive'}
              </span>
            } />
            <InfoRow label="From Email" value={zeptomail.from_email} mono />
            <InfoRow label="From Name" value={zeptomail.from_name} />
          </div>
        </div>

        {/* ElevenLabs Integration */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <SpeakerWaveIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">ElevenLabs</h3>
                <p className="text-xs text-slate-500">Voice synthesis (text-to-speech)</p>
              </div>
            </div>
            <StatusBadge status={elevenlabs.status} />
          </div>

          <div className="space-y-0">
            {elevenlabs.masked_key && (
              <InfoRow label="API Key" value={
                <code className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-700">
                  {'*'.repeat(16)}{elevenlabs.masked_key.replace('...', '')}
                </code>
              } />
            )}
            <InfoRow label="Voice Chat" value={
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                elevenlabs.voice_enabled ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
              }`}>
                {elevenlabs.voice_enabled ? 'Enabled' : 'Disabled'}
              </span>
            } />
            {elevenlabs.status === 'not_configured' && (
              <div className="mt-4 bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">
                  Set the <code className="px-1 py-0.5 bg-slate-200 rounded text-[10px]">ELEVENLABS_API_KEY</code> environment variable to enable voice features.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Platform Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <CogIcon className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Platform Configuration</h3>
            <p className="text-xs text-slate-500">Feature flags, RAG settings, and security</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feature Flags */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BoltIcon className="h-4 w-4" />
              Feature Flags
            </h4>
            <div className="space-y-0">
              <FeatureFlag label="Voice Chat" enabled={platform.feature_flags.voice_chat} />
              <FeatureFlag label="Multilingual" enabled={platform.feature_flags.multilingual} />
              <FeatureFlag label="Advanced Analytics" enabled={platform.feature_flags.advanced_analytics} />
              <FeatureFlag label="Debug Mode" enabled={platform.debug_mode} />
            </div>
          </div>

          {/* RAG Settings */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              RAG Settings
            </h4>
            <div className="space-y-0">
              <InfoRow label="Chunk Strategy" value={platform.rag.chunk_strategy} />
              <InfoRow label="Chunk Size" value={platform.rag.chunk_size} />
              <InfoRow label="Chunk Overlap" value={platform.rag.chunk_overlap} />
              <InfoRow label="Top-K Results" value={platform.rag.retrieval_top_k} />
              <InfoRow label="Similarity" value={platform.rag.similarity_threshold} />
              <InfoRow label="MMR" value={platform.rag.use_mmr ? 'Enabled' : 'Disabled'} />
            </div>
          </div>

          {/* Security */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <ShieldCheckIcon className="h-4 w-4" />
              Security
            </h4>
            <div className="space-y-0">
              <InfoRow label="JWT Algorithm" value={platform.security.jwt_algorithm} mono />
              <InfoRow label="Token Expiry" value={`${platform.security.access_token_expire_minutes}m`} />
              <InfoRow label="Refresh Expiry" value={`${platform.security.refresh_token_expire_days}d`} />
              <InfoRow label="Rate Limit" value={`${platform.security.rate_limit_per_minute}/min`} />
              <InfoRow label="Chat Rate" value={`${platform.security.chat_rate_limit_per_minute}/min`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
